import { memo, useCallback, useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useNavTitle } from '../lib/nav'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast, type ToastApi, Button, InleagueLastRead } from '@region13/ui'
import { PHONE_TAP } from '../components/ui'
import { jerseyNumberGroups, matchDesignation, nameWithPreferred, teamLabel } from '@region13/shared-types'
import { api, ApiError, type CoachTeamWithGames, type JerseyEntryV, type RosterPlayerV } from '../lib/api'
import { invalidateCoachLists } from '../lib/query'

export const Route = createFileRoute('/jerseys')({
  component: Jerseys,
  // Home deep-links here with ?team=<ext> to preselect a team.
  validateSearch: (s: Record<string, unknown>): { team?: string } => ({
    team: typeof s.team === 'string' ? s.team : undefined,
  }),
})

function toastSaveError(toast: ToastApi, err: unknown) {
  toast.error(err instanceof ApiError ? err.message : 'Could not save jersey numbers.')
}

const selectClass =
  'min-h-[44px] rounded-lg border border-line bg-surface px-3 text-sm text-ink ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-maroon/30 focus:border-brand-maroon'

const jerseyInputClass =
  'w-16 min-h-[44px] rounded-lg border bg-surface px-2 text-center text-base text-ink ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-maroon/30 focus:border-brand-maroon'

const playerName = (p: RosterPlayerV) => nameWithPreferred(p.firstName, p.preferredName, p.lastName)

/** First · Preferred · Last · number. Last gets the most room and Preferred
 *  (empty on most rows) the least, so a ten-letter last name fits on one line at
 *  375px (about 74 / 56 / 93px). `minmax(0, …)` plus `break-words` still keeps a
 *  single word too long for its column from pushing the input off screen. */
const ROSTER_GRID = 'grid grid-cols-[minmax(0,1fr)_minmax(0,0.75fr)_minmax(0,1.25fr)_auto] items-center gap-2 sm:gap-3'

/** The save payload from the draft fields. A blank field is skipped by its
 *  text, not its value: `Number('')` is 0, and #0 is a real jersey. The input
 *  takes two digits only, so every non-blank field is 0-99. */
export function draftEntries(players: readonly RosterPlayerV[], valueFor: (p: RosterPlayerV) => string): JerseyEntryV[] {
  return players
    .filter((p) => valueFor(p) !== '')
    .map((p) => ({ player_id: p.childId, registration_id: p.registrationId, jersey_number: Number(valueFor(p)) }))
}

/** Keyed by childId: the names of the teammates who share that player's number,
 *  joined into a string so the memoized row still compares equal between
 *  keystrokes. Same rule as the server's refusal (`jerseyNumberGroups`). */
export function sharedNumbers(players: readonly RosterPlayerV[], valueFor: (p: RosterPlayerV) => string): Map<string, string> {
  const filled = players.filter((p) => valueFor(p) !== '')
  const out = new Map<string, string>()
  for (const holders of jerseyNumberGroups(filled, (p) => Number(valueFor(p))).values()) {
    for (const p of holders) out.set(p.childId, holders.filter((o) => o !== p).map(playerName).join(', '))
  }
  return out
}

/** One roster row: player name + an editable jersey-number field. Digits
 *  only and at most two (stripped as typed), so a stray letter or a 100 never
 *  reaches a save payload; a shared number is outlined (`sharedWith`). Memoized
 *  with a stable `onChange`, so a keystroke re-renders only that row. */
export const PlayerRow = memo(function PlayerRow({
  player,
  value,
  sharedWith,
  onChange,
}: {
  player: RosterPlayerV
  value: string
  sharedWith?: string
  onChange: (childId: string, value: string) => void
}) {
  const name = playerName(player)
  const shared = Boolean(sharedWith)
  const noteId = `jersey-shared-${player.childId}`
  return (
    <div className={`${ROSTER_GRID} px-4 py-3`}>
      <span className="break-words text-sm font-semibold text-ink">{player.firstName}</span>
      <span className="break-words text-sm text-ink">{player.preferredName ?? ''}</span>
      <span className="break-words text-sm font-semibold text-ink">{player.lastName}</span>
      <input
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        aria-label={`Jersey number for ${name}`}
        aria-invalid={shared || undefined}
        aria-describedby={shared ? noteId : undefined}
        value={value}
        onChange={(e) => onChange(player.childId, e.target.value.replace(/\D/g, ''))}
        className={`${jerseyInputClass} ${shared ? 'border-brand-red-dark' : 'border-line'}`}
      />
      {shared ? (
        <span id={noteId} className="col-span-4 text-xs text-brand-red-dark">
          Also #{value}: {sharedWith}
        </span>
      ) : null}
    </div>
  )
})

/** The team Home's `?team=` deep link selects: the coach's own team in either
 *  spelling (a link saved before team ids became `B10-01` still says `B10U01`,
 *  #881), else the first team, as with no link. Null with no teams. */
export function deepLinkedTeam(teamParam: string | undefined, teams: readonly CoachTeamWithGames[]): string | null {
  const ids = teams.map((t) => t.externalId)
  return (teamParam ? matchDesignation(teamParam, ids) : null) ?? ids[0] ?? null
}

/** The team picker atop the roster: one labeled `<select>`, teamLabel'd like
 *  every other team header on this portal. Exported (not module-private) so
 *  a test can assert its rendered option text directly, the same way
 *  `index.tsx`/`cards.tsx`/`referees.tsx` export their own team headers -
 *  reaching it through `Jerseys` would drag in the router search param and
 *  both queries that component depends on. */
export function TeamPicker({
  teams,
  activeExt,
  onChange,
}: {
  teams: CoachTeamWithGames[]
  activeExt: string | null
  onChange: (ext: string) => void
}) {
  return (
    <label className="flex flex-col gap-1 text-xs font-semibold text-ink-soft">
      Team
      <select
        className={selectClass}
        value={activeExt ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {teams.map((t) => (
          <option key={t.externalId} value={t.externalId}>
            {teamLabel({ designation: t.externalId, coachName: t.coachName, teamName: t.name })}
          </option>
        ))}
      </select>
    </label>
  )
}

/** Lets a coach pick one of their teams, edit each player's jersey number,
 *  and save the whole roster in one call. Consumes `api.teams`/`api.roster`/
 *  `api.saveJerseys` (`lib/api.ts`); on the team list, teams carry
 *  `externalId`/`name` (not `teamDesignation`/`teamName`). */
function Jerseys() {
  const title = useNavTitle()
  const toast = useToast()
  const queryClient = useQueryClient()

  const teamsQ = useQuery({ queryKey: ['teams'], queryFn: api.teams })
  const teams = teamsQ.data?.teams ?? []

  // Preselect the team from Home's ?team= deep link (falls back to the first).
  const { team: teamParam } = Route.useSearch()
  const [selectedExt, setSelectedExt] = useState<string | null>(null)
  const activeExt = selectedExt ?? deepLinkedTeam(teamParam, teams)

  const rosterQ = useQuery({
    queryKey: ['roster', activeExt],
    queryFn: () => api.roster(activeExt!),
    enabled: activeExt !== null,
  })
  const players = rosterQ.data?.players ?? []

  // Per-player draft values, keyed by childId. Reset whenever the active
  // team changes so a switch never leaves another team's edits pending.
  const [edits, setEdits] = useState<Record<string, string>>({})
  useEffect(() => {
    setEdits({})
  }, [activeExt])

  function valueFor(p: RosterPlayerV): string {
    return edits[p.childId] ?? (p.jersey == null ? '' : String(p.jersey))
  }

  // Stable across renders so the memoized rows don't all re-render on a keystroke.
  const handleEdit = useCallback((childId: string, value: string) => {
    setEdits((prev) => ({ ...prev, [childId]: value }))
  }, [])

  const shared = sharedNumbers(players, valueFor)

  const saveMutation = useMutation({
    mutationFn: () => api.saveJerseys(activeExt!, draftEntries(players, valueFor)),
    onSuccess: () => {
      toast.success('Jersey numbers saved.')
      setEdits({})
      invalidateCoachLists(queryClient)
    },
    onError: (err: unknown) => toastSaveError(toast, err),
  })

  const heading = (
    <div>
      <h1 className="text-lg font-extrabold text-ink">{title}</h1>
      <InleagueLastRead step="players" className="mt-1" />
    </div>
  )

  if (teamsQ.isLoading) {
    return (
      <div className="flex flex-col gap-6">
        {heading}
        <p role="status" className="text-sm text-ink-soft">Loading your teams…</p>
      </div>
    )
  }

  if (teams.length === 0) {
    return (
      <div className="flex flex-col gap-6">
        {heading}
        <p className="text-sm text-ink-soft">You are not listed as a coach for any team this season.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {heading}

      <TeamPicker teams={teams} activeExt={activeExt} onChange={setSelectedExt} />

      {rosterQ.isLoading ? (
        <p role="status" className="text-sm text-ink-soft">Loading roster…</p>
      ) : players.length === 0 ? (
        <p className="text-sm text-ink-soft">No players on this roster yet.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          <div aria-hidden="true" className={`${ROSTER_GRID} border-b border-line px-4 py-2 text-xs font-semibold text-ink-soft`}>
            <span>First</span>
            <span>Preferred</span>
            <span>Last</span>
            <span className="w-16 text-center">#</span>
          </div>
          <div className="divide-y divide-line">
            {players.map((p) => (
              <PlayerRow
                key={p.childId}
                player={p}
                value={valueFor(p)}
                sharedWith={shared.get(p.childId)}
                onChange={handleEdit}
              />
            ))}
          </div>
        </div>
      )}

      {shared.size > 0 ? (
        <p role="status" className="text-sm text-brand-red-dark">
          Two players cannot share a jersey number. Change one of the outlined numbers to save.
        </p>
      ) : null}

      <Button className={PHONE_TAP} variant="primary"
        disabled={!activeExt || players.length === 0 || shared.size > 0 || saveMutation.isPending}
        onClick={() => saveMutation.mutate()}
      >
        {saveMutation.isPending ? 'Saving…' : 'Save jersey numbers'}
      </Button>
    </div>
  )
}
